import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Plus, Copy, Navigation, Building2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function RateChartMaster({
  currentTenant,
  centers = [],
  lines = [],
  products = [],
  lang = 'mr',
  currentUser
}) {
  const isMr = lang === 'mr';
  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [rateCharts, setRateCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCenter, setSelectedCenter] = useState('ALL');
  const [selectedLine, setSelectedLine] = useState('ALL');
  const [statusMsg, setStatusMsg] = useState(null);

  // Add Rate Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    center_id: '',
    line_id: '',
    product_id: '',
    rate: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: ''
  });

  // Copy Rate Modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyForm, setCopyForm] = useState({
    source_line_id: '',
    target_line_id: '',
    effective_from: new Date().toISOString().split('T')[0]
  });

  const [submitting, setSubmitting] = useState(false);

  // Internal products state with auto-fetch fallback if products prop is empty
  const [internalProducts, setInternalProducts] = React.useState(products && products.length > 0 ? products : []);

  React.useEffect(() => {
    if (products && products.length > 0) {
      setInternalProducts(products);
    } else if (tenantSlug) {
      fetch(`${API_BASE}/tenant/${tenantSlug}/products`)
        .then(res => res.json())
        .then(data => {
          if (data.success && Array.isArray(data.products) && data.products.length > 0) {
            setInternalProducts(data.products);
          }
        })
        .catch(err => console.warn('Could not auto-fetch products:', err));
    }
  }, [products, tenantSlug]);

  // Robust Milk Products Filtering (Supports English & Marathi names, units & categories)
  const milkProducts = React.useMemo(() => {
    const list = internalProducts || [];
    const filtered = list.filter(p => {
      const cat = (p.category || '').toLowerCase();
      const name = (p.name_mr || p.name || p.name_en || '').toLowerCase();
      const unit = (p.unit_mr || p.unit_en || p.unit || '').toLowerCase();
      return (
        cat === 'milk' || 
        cat === 'दूध' || 
        name.includes('दूध') || 
        name.includes('milk') || 
        name.includes('गाय') || 
        name.includes('म्हैस') || 
        name.includes('cow') || 
        name.includes('buff') ||
        unit.includes('लिटर') || 
        unit.includes('liter') || 
        unit.includes('ltr')
      );
    });
    // Fallback: If heuristic found no items, show all products so user is never stuck with empty dropdown
    return filtered.length > 0 ? filtered : list;
  }, [internalProducts]);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/tenant/${tenantSlug}/rate-charts`;
      const params = [];
      if (selectedLine !== 'ALL') params.push(`line_id=${selectedLine}`);
      if (selectedCenter !== 'ALL') params.push(`center_id=${selectedCenter}`);
      if (params.length > 0) url += '?' + params.join('&');

      const res = await fetch(url);
      const data = await res.json();
      if (data.success && Array.isArray(data.rate_charts)) {
        setRateCharts(data.rate_charts);
      }
    } catch (err) {
      console.error('Error fetching rates:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, selectedLine, selectedCenter]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Handle Add Rate
  const handleSaveRate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/rate-charts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg({ type: 'success', text: isMr ? 'नवीन दर यशस्वीरित्या लागू करण्यात आला!' : 'Rate saved successfully!' });
        setShowAddModal(false);
        fetchRates();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Error saving rate' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Copy Rate Chart
  const handleCopyRate = async (e) => {
    e.preventDefault();
    if (!copyForm.source_line_id || !copyForm.target_line_id) {
      alert(isMr ? 'कृपया मूळ लाईन आणि लक्ष्य लाईन दोन्ही निवडा.' : 'Please select both source and target lines.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/rate-charts/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(copyForm)
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        setShowCopyModal(false);
        fetchRates();
      } else {
        setStatusMsg({ type: 'error', text: data.message });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={22} color="#0284c7" />
            <h1 style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a', margin: 0 }}>
              {isMr ? 'दूध दरपत्रक व्यवस्थापन' : 'Milk Rate Chart Master'}
            </h1>
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>
            {isMr 
              ? 'सेंटर व लाईननुसार तारीख-आधारित दुधाचे दर (गाय दूध व म्हैस दूध) सेट करा किंवा दुसऱ्या लाईनला कॉपी करा' 
              : 'Line & Center date-effective milk rates with instant cross-line copy'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setCopyForm({
                source_line_id: lines[0]?.id || '',
                target_line_id: lines[1]?.id || '',
                effective_from: new Date().toISOString().split('T')[0]
              });
              setShowCopyModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0.5rem 1rem',
              borderRadius: 8,
              border: '1px solid #bae6fd',
              background: '#f0f9ff',
              color: '#0284c7',
              fontSize: '0.82rem',
              fontWeight: 800,
              cursor: 'pointer'
            }}
          >
            <Copy size={15} />
            <span>{isMr ? '📋 दरपत्रक कॉपी करा' : 'Copy Rate Chart'}</span>
          </button>

          <button
            onClick={() => {
              setAddForm({
                center_id: centers[0]?.id || '',
                line_id: lines[0]?.id || '',
                product_id: milkProducts[0]?.id || '',
                rate: milkProducts[0]?.retail_rate || '56.00',
                effective_from: new Date().toISOString().split('T')[0],
                effective_to: ''
              });
              setShowAddModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0.5rem 1rem',
              borderRadius: 8,
              border: 'none',
              background: '#0284c7',
              color: '#ffffff',
              fontSize: '0.82rem',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)'
            }}
          >
            <Plus size={16} />
            <span>{isMr ? '+ नवीन दर लागू करा' : '+ Add New Rate'}</span>
          </button>
        </div>
      </div>

      {/* Quick Guide Info Box */}
      <div style={{
        background: '#f0fdf4',
        border: '1.5px solid #86efac',
        borderRadius: 14,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem' }}>
          💡
        </div>
        <div style={{ fontSize: '0.82rem', color: '#166534', lineHeight: 1.5 }}>
          <div style={{ fontWeight: 900, fontSize: '0.92rem', color: '#14532d', marginBottom: 2 }}>
            {isMr ? 'दूध दरपत्रक कसे वापरावे?' : 'How to Manage Milk Rate Charts?'}
          </div>
          {isMr ? (
            <div>
              <strong>१. नवीन दर लागू करणे:</strong> वरील <strong>"+ नवीन दर लागू करा"</strong> बटणावर क्लिक करा. योग्य लाईन (मार्ग) व दूध प्रकार (उदा. गाय किंवा म्हैस दूध) निवडा. नवीन विक्री दर आणि लागू होण्याची तारीख सेट करा. त्या तारखेपासून त्या मार्गावरील सर्व ग्राहकांच्या वाटपाला व बिलांना नवीन दर आपोआप लागू होईल.<br/>
              <strong>२. दरपत्रक कॉपी करणे:</strong> जर तुमच्याकडे अनेक मार्ग (Lines) असतील, तर एका मार्गाचे दर इतर सर्व मार्गांवर <strong>"📋 दरपत्रक कॉपी करा"</strong> बटणाने एका सेकंदात लागू करता येतील.
            </div>
          ) : (
            <div>
              Set date-effective milk rates line-wise, or clone rates from one route to another instantly. All daily deliveries on or after the effective date will use the new rate automatically.
            </div>
          )}
        </div>
      </div>

      {/* Alert */}
      {statusMsg && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 700,
          background: statusMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: statusMsg.type === 'success' ? '#15803d' : '#b91c1c',
          border: '1px solid ' + (statusMsg.type === 'success' ? '#bbf7d0' : '#fecdd3')
        }}>
          {statusMsg.text}
        </div>
      )}

      {/* Filter Toolbar */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '0.85rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Building2 size={16} color="#64748b" />
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>
            {isMr ? 'सेंटर:' : 'Center:'}
          </span>
          <select
            value={selectedCenter}
            onChange={e => setSelectedCenter(e.target.value)}
            style={{ padding: '0.4rem 0.65rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
          >
            <option value="ALL">{isMr ? 'सर्व सेंटर्स (All Centers)' : 'All Centers'}</option>
            {centers.map(c => (
              <option key={c.id} value={c.id}>{isMr ? (c.name_mr || c.name) : (c.name_en || c.name)}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Navigation size={16} color="#64748b" />
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>
            {isMr ? 'लाईन / मार्ग:' : 'Delivery Line:'}
          </span>
          <select
            value={selectedLine}
            onChange={e => setSelectedLine(e.target.value)}
            style={{ padding: '0.4rem 0.65rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
          >
            <option value="ALL">{isMr ? 'सर्व लाईन्स (All Lines)' : 'All Lines'}</option>
            {lines.map(l => (
              <option key={l.id} value={l.id}>{isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Rate Table */}
      <div style={{ background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'दूध उत्पादन' : 'Milk Product'}</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'लाईन / मार्ग' : 'Line'}</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'सेंटर' : 'Center'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'विक्री दर (₹/L)' : 'Rate (₹/L)'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'लागू दिनांक' : 'Effective From'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'अखेर दिनांक' : 'Effective To'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'स्थिती' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    {isMr ? 'दरपत्रक लोड होत आहे...' : 'Loading rate charts...'}
                  </td>
                </tr>
              ) : rateCharts.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                    {isMr ? 'कोणतेही दरपत्रक सापडले नाही.' : 'No rate chart entries found.'}
                  </td>
                </tr>
              ) : (
                rateCharts.map(r => {
                  const isActive = !r.effective_to || new Date(r.effective_to) >= new Date();
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <strong style={{ color: '#0f172a', fontSize: '0.88rem' }}>
                          {isMr ? r.product_name_mr : (r.product_name_en || r.product_name_mr)}
                        </strong>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{r.product_code}</div>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        {r.line_name_mr || (isMr ? 'सर्व लाईन्स' : 'All Lines')}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', color: '#64748b' }}>
                        {r.center_name_mr || (isMr ? 'सर्व सेंटर्स' : 'All Centers')}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 900, color: '#0284c7', fontSize: '1rem' }}>
                        ₹{parseFloat(r.rate).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontFamily: 'monospace' }}>
                        {r.effective_from}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontFamily: 'monospace', color: r.effective_to ? '#e11d48' : '#059669' }}>
                        {r.effective_to || (isMr ? 'सुरू (चालू दर)' : 'Active (Ongoing)')}
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                        <span style={{
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: isActive ? '#dcfce7' : '#f1f5f9',
                          color: isActive ? '#15803d' : '#64748b'
                        }}>
                          {isActive ? (isMr ? 'चालू' : 'Active') : (isMr ? 'समाप्त' : 'Expired')}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal 1: Add / Set New Rate */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '1rem', fontWeight: 800, fontSize: '1rem' }}>
              {isMr ? 'नवीन दूध दर लागू करा' : 'Set New Milk Rate'}
            </div>
            <form onSubmit={handleSaveRate} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'दूध उत्पादन निवडा (Select Product):' : 'Select Milk Product:'}
                </label>
                <select
                  value={addForm.product_id}
                  onChange={e => {
                    const selId = e.target.value;
                    const selP = milkProducts.find(item => String(item.id) === String(selId));
                    setAddForm({ 
                      ...addForm, 
                      product_id: selId, 
                      rate: selP ? (selP.retail_rate || selP.retailRate || addForm.rate) : addForm.rate 
                    });
                  }}
                  required
                  style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.85rem', fontWeight: 700 }}
                >
                  <option value="">{isMr ? '-- दूध प्रकार निवडा (गाय / म्हैस) --' : '-- Select Milk Type --'}</option>
                  {milkProducts.map(p => {
                    const curRate = parseFloat(p.retail_rate || p.retailRate || 0).toFixed(2);
                    const pName = isMr ? (p.name_mr || p.name) : (p.name_en || p.name);
                    return (
                      <option key={p.id} value={p.id}>
                        🥛 {pName} (चालू दर: ₹${curRate} {p.unit_mr ? `/ ${p.unit_mr}` : ''})
                      </option>
                    );
                  })}
                </select>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3 }}>
                  💡 {isMr ? 'उत्पादन निवडताच त्याचा सध्याचा दर खाली आपोआप येईल. आपण नवीन दर बदलू शकता.' : 'Current base rate will be auto-filled in rate field below.'}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'वितरण लाईन निवडा:' : 'Select Delivery Line:'}
                </label>
                <select
                  value={addForm.line_id}
                  onChange={e => setAddForm({ ...addForm, line_id: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                >
                  <option value="">{isMr ? 'सर्व लाईन्स (All Lines)' : 'All Lines'}</option>
                  {lines.map(l => (
                    <option key={l.id} value={l.id}>{isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'नवीन विक्री दर (₹ प्रति लिटर):' : 'New Rate (₹ per Litre):'}
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="1"
                  required
                  value={addForm.rate}
                  onChange={e => setAddForm({ ...addForm, rate: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: 800, color: '#0284c7', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'लागू होण्याची तारीख (Effective From):' : 'Effective From Date:'}
                </label>
                <input
                  type="date"
                  required
                  value={addForm.effective_from}
                  onChange={e => setAddForm({ ...addForm, effective_from: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{ padding: '0.45rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700 }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: '0.45rem 1.25rem', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800 }}
                >
                  {submitting ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') : (isMr ? 'दर सेव्ह करा' : 'Save Rate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Copy Rate Chart to Another Line */}
      {showCopyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '1rem', fontWeight: 800, fontSize: '1rem' }}>
              {isMr ? '📋 एका लाईनचे दरपत्रक दुसऱ्या लाईनला कॉपी करा' : 'Copy Rate Chart Between Lines'}
            </div>
            <form onSubmit={handleCopyRate} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', background: '#f0f9ff', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #bae6fd' }}>
                💡 {isMr 
                  ? 'मूळ लाईनवरील सर्व दुधाचे चालू दर लक्ष्य लाईनवर एका क्लिकमध्ये लागू होतील. यामुळे वेळ व कष्ट वाचतील.' 
                  : 'All active milk rates from the source line will be cloned to the target line instantly.'}
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'मूळ लाईन (Source Line - ज्यावरून दर घ्यायचे आहेत):' : 'Source Line (Copy from):'}
                </label>
                <select
                  value={copyForm.source_line_id}
                  onChange={e => setCopyForm({ ...copyForm, source_line_id: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                >
                  <option value="">{isMr ? '-- मूळ लाईन निवडा --' : '-- Select Source Line --'}</option>
                  {lines.map(l => (
                    <option key={l.id} value={l.id}>{isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'लक्ष्य लाईन (Target Line - ज्या लाईनवर दर लागू करायचे आहेत):' : 'Target Line (Copy to):'}
                </label>
                <select
                  value={copyForm.target_line_id}
                  onChange={e => setCopyForm({ ...copyForm, target_line_id: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
                >
                  <option value="">{isMr ? '-- लक्ष्य लाईन निवडा --' : '-- Select Target Line --'}</option>
                  {lines.filter(l => l.id.toString() !== copyForm.source_line_id.toString()).map(l => (
                    <option key={l.id} value={l.id}>{isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'लागू होण्याची तारीख (Effective From):' : 'Effective From Date:'}
                </label>
                <input
                  type="date"
                  required
                  value={copyForm.effective_from}
                  onChange={e => setCopyForm({ ...copyForm, effective_from: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowCopyModal(false)}
                  style={{ padding: '0.45rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700 }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: '0.45rem 1.25rem', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800 }}
                >
                  {submitting ? (isMr ? 'कॉपी होत आहे...' : 'Copying...') : (isMr ? 'दरपत्रक कॉपी करा' : 'Copy Rates Now')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
